import {
	faAlignCenter,
	faAlignJustify,
	faAlignLeft,
	faAlignRight,
	faBold,
	faBracketsCurly,
	faEraser,
	faFont,
	faHighlighter,
	faImage,
	faItalic,
	faLink,
	faListOl,
	faListUl,
	faPalette,
	faRedo,
	faStrikethrough,
	faTextHeight,
	faUnderline,
	faUndo,
	faUnlink,
} from '@fortawesome/pro-solid-svg-icons';
import {
	type MediaAssetDto,
	PermissionService,
	RbacActions,
} from '@supply-mover-2-0/shared';
import { Color } from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, Extension, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Dropdown, Icon, useThemeColors, useTranslation } from '../..';
import type { AvailableVariable } from '../../services/templateContextAnalyzer';
import { MediaPickerModal } from '../media/MediaPickerModal';
import { ContextAwareMergeVariableDropdown } from './ContextAwareMergeVariableDropdown';

interface TemplateVariable {
	name: string;
	type: string;
	required: boolean;
	defaultValue?: unknown;
	description?: string;
}

/**
 * A toolbar control, for callers that must not offer every kind of formatting.
 *
 * The website editor is the reason this exists: a site's schema says which marks a field
 * permits, and anything else is dropped by the sanitizer when the draft is saved. Offering
 * a Font Color button whose result silently disappears is worse than not offering it.
 */
export const RICH_TEXT_EDITOR_TOOLS = [
	'bold',
	'italic',
	'underline',
	'strikethrough',
	'font',
	'color',
	'lists',
	'align',
	'link',
	'image',
	'clear',
	'history',
] as const;

export type RichTextEditorTool = (typeof RICH_TEXT_EDITOR_TOOLS)[number];

interface RichTextEditorProps {
	value: string;
	onChange: (html: string) => void;
	placeholder?: string;
	minHeight?: string;
	extensions?: Extension[];
	showMergeVariableButton?: boolean;
	availableVariables?: Array<TemplateVariable>;
	/** Context-aware variables with type information for nested template support */
	contextAwareVariables?: Array<AvailableVariable>;
	onInsertVariable?: (variableName: string) => void;
	className?: string;
	disabled?: boolean;
	/**
	 * Which toolbar controls to offer. Omit for all of them, which is what every existing
	 * caller gets; pass a list to offer only those.
	 */
	tools?: ReadonlyArray<RichTextEditorTool>;
	/**
	 * Where "Insert image" takes a picture from. `embed` (the default) puts the chosen file
	 * into the text itself, which suits private text such as notes. `media-library` (PD-16339)
	 * opens the media library picker and inserts the item's email-safe public address: for
	 * email templates, whose pictures must be public, stay current when replaced, and cannot
	 * be deleted while in use. Without the library's view permission the button is hidden.
	 */
	imageSource?: 'embed' | 'media-library';
}

const fontSizes = [
	{ label: 'Small', value: '12px' },
	{ label: 'Normal', value: '14px' },
	{ label: 'Large', value: '16px' },
	{ label: 'Extra Large', value: '18px' },
	{ label: 'Huge', value: '24px' },
];

/**
 * Registers a `fontSize` attribute on the textStyle mark. The base
 * TextStyle extension defines NO attributes of its own — Tiptap silently
 * drops attributes a mark hasn't registered, so the toolbar's
 * `.setMark('textStyle', { fontSize })` produced a bare <span> and the
 * Font Size menu never changed anything. (Color and FontFamily work
 * because their extensions register their attributes the same way.)
 */
const FontSize = Extension.create({
	name: 'fontSize',
	addGlobalAttributes() {
		return [
			{
				types: ['textStyle'],
				attributes: {
					fontSize: {
						default: null,
						parseHTML: (element: HTMLElement): string | null =>
							element.style.fontSize !== '' ? element.style.fontSize : null,
						renderHTML: (attributes: {
							fontSize?: string | null;
						}): Record<string, string> => {
							if (attributes.fontSize == null) {
								return {};
							}
							return { style: `font-size: ${attributes.fontSize}` };
						},
					},
				},
			},
		];
	},
});

const fontFamilies = [
	{ label: 'Default', value: 'inherit' },
	{ label: 'Arial', value: 'Arial, sans-serif' },
	{ label: 'Times New Roman', value: 'Times New Roman, serif' },
	{ label: 'Courier New', value: 'Courier New, monospace' },
	{ label: 'Georgia', value: 'Georgia, serif' },
	{ label: 'Verdana', value: 'Verdana, sans-serif' },
];

const textColors = [
	'#000000',
	'#434343',
	'#666666',
	'#999999',
	'#cccccc',
	'#ff0000',
	'#ff9900',
	'#ffff00',
	'#00ff00',
	'#00ffff',
	'#0000ff',
	'#9900ff',
	'#ff00ff',
	'#f44336',
	'#e91e63',
	'#9c27b0',
	'#673ab7',
	'#3f51b5',
	'#2196f3',
	'#03a9f4',
];

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
	value,
	onChange,
	placeholder = '',
	minHeight = '300px',
	extensions = [],
	showMergeVariableButton = false,
	availableVariables = [],
	contextAwareVariables,
	onInsertVariable,
	className = '',
	disabled = false,
	tools,
	imageSource = 'embed',
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
	const usesMediaLibrary = imageSource === 'media-library';

	const offers = useCallback(
		(tool: RichTextEditorTool): boolean =>
			(tools == null || tools.includes(tool)) &&
			(tool !== 'image' ||
				!usesMediaLibrary ||
				PermissionService.can(RbacActions.MEDIA.READ)),
		[tools, usesMediaLibrary],
	);

	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);
	const [showColorPicker, setShowColorPicker] = useState(false);
	const [showHighlightPicker, setShowHighlightPicker] = useState(false);
	const [showVariablePicker, setShowVariablePicker] = useState(false);
	const [cursorPosition, setCursorPosition] = useState(0);
	const [textContent, setTextContent] = useState('');
	// PD-16748 — the placeholder used to be accepted and never shown (there
	// is no Tiptap placeholder extension in the app), so an empty editor gave
	// no hint of what to type. It is drawn over the empty document instead.
	const [isEmpty, setIsEmpty] = useState(true);

	const defaultExtensions = [
		StarterKit.configure({
			link: false,
			underline: false,
		}),
		Underline,
		Link.configure({
			openOnClick: false,
			HTMLAttributes: {
				class: 'text-primary-600 underline',
			},
		}),
		TextStyle,
		FontSize,
		Color,
		TextAlign.configure({
			types: ['heading', 'paragraph'],
		}),
		FontFamily,
		Image.configure({
			HTMLAttributes: {
				class: 'max-w-full h-auto',
			},
		}),
		...extensions,
	];

	const editor = useEditor(
		{
			extensions: defaultExtensions,
			content: value,
			editable: !disabled,
			editorProps: {
				attributes: {
					class: `prose prose-sm dark:prose-invert max-w-none focus:outline-none p-4 h-full ${colors.classes.textPrimary} ${disabled ? 'opacity-75 cursor-not-allowed' : ''} ${className}`,
					style: `min-height: ${minHeight}`,
				},
			},
			onUpdate: ({ editor: currentEditor }) => {
				onChange(currentEditor.getHTML());
				// Update text content for context-aware variable picker
				if (contextAwareVariables != null && contextAwareVariables.length > 0) {
					setTextContent(currentEditor.getText());
				}
			},
			onCreate: ({ editor: currentEditor }) => {
				setIsEmpty(currentEditor.isEmpty);
			},
			onTransaction: ({ editor: currentEditor }) => {
				setCanUndo(currentEditor.can().undo());
				setCanRedo(currentEditor.can().redo());
				setIsEmpty(currentEditor.isEmpty);
				// Track cursor position for context-aware variable picker
				if (contextAwareVariables != null && contextAwareVariables.length > 0) {
					const { from } = currentEditor.state.selection;
					// Convert HTML position to approximate text position
					const textBeforeCursor = currentEditor.state.doc.textBetween(
						0,
						from,
						' ',
					);
					setCursorPosition(textBeforeCursor.length);
				}
			},
			onSelectionUpdate: ({ editor: currentEditor }) => {
				// Also update on selection changes
				if (contextAwareVariables != null && contextAwareVariables.length > 0) {
					const { from } = currentEditor.state.selection;
					const textBeforeCursor = currentEditor.state.doc.textBetween(
						0,
						from,
						' ',
					);
					setCursorPosition(textBeforeCursor.length);
					setTextContent(currentEditor.getText());
				}
			},
		},
		[disabled, contextAwareVariables],
	);

	// Sync editor content when value prop changes. `emitUpdate: false`: a value arriving from
	// outside is not an edit. Tiptap 3 emits by default, which sent the editor's own
	// re-serialized HTML (list items wrapped in paragraphs, a trailing empty paragraph) back
	// through onChange as though the person had typed it.
	// Skip a destroyed editor: when `disabled` and `value` change in the same
	// render (a reply box that clears itself as sending finishes), useEditor
	// destroys the old instance in its own effect and this effect still sees
	// that instance. Its schema is gone, so getHTML() throws and takes the
	// whole page down. The replacement editor is created from the current
	// `value`, and this effect runs again once it arrives.
	useEffect(() => {
		if (editor != null && !editor.isDestroyed && value !== editor.getHTML()) {
			editor.commands.setContent(value, { emitUpdate: false });
		}
	}, [value, editor]);

	const addLink = useCallback(() => {
		const previousUrl = editor?.getAttributes('link')['href'];
		const url = window.prompt(t('Enter URL'), previousUrl);

		if (url === null) {
			return;
		}

		if (url === '') {
			editor?.chain().focus().extendMarkRange('link').unsetLink().run();
			return;
		}

		editor
			?.chain()
			.focus()
			.extendMarkRange('link')
			.setLink({ href: url })
			.run();
	}, [editor, t]);

	const insertImage = useCallback(async () => {
		if (usesMediaLibrary) {
			setIsMediaPickerOpen(true);
			return;
		}
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				const reader = new FileReader();
				reader.onload = () => {
					const dataUrl = reader.result as string;
					editor
						?.chain()
						.focus()
						.setImage({
							src: dataUrl,
							alt: file.name,
						})
						.run();
				};
				reader.readAsDataURL(file);
			}
		};
		input.click();
	}, [editor, usesMediaLibrary]);

	const insertLibraryPicture = useCallback(
		(asset: MediaAssetDto): void => {
			const src = asset.urls?.email ?? asset.urls?.main;
			if (src == null) {
				return;
			}
			editor
				.chain()
				.focus()
				.setImage({ src, alt: asset.altText ?? asset.title })
				.run();
		},
		[editor],
	);

	const insertVariable = useCallback(
		(variableName: string) => {
			if (editor != null) {
				editor.chain().focus().insertContent(`{{${variableName}}}`).run();
				setShowVariablePicker(false);

				if (onInsertVariable != null) {
					onInsertVariable(variableName);
				}
			}
		},
		[editor, onInsertVariable],
	);

	/**
	 * The ProseMirror element only grows to fit its content, so the rest of
	 * the scroll area below it is inert — clicking there looks like clicking
	 * the writing surface but does nothing. Treat any click that lands
	 * outside the content node as "put the cursor at the end", which is how
	 * every mail/document editor behaves. Done on mousedown (not click) so
	 * the caret lands before the browser's own focus handling runs.
	 */
	const handleSurfaceMouseDown = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (disabled || editor == null) {
				return;
			}

			if ((event.target as HTMLElement).closest('.ProseMirror') != null) {
				return;
			}

			event.preventDefault();
			editor.chain().focus('end').run();
		},
		[disabled, editor],
	);

	// Handler for context-aware variable insertion
	const handleContextAwareInsert = useCallback(
		(text: string, cursorOffset?: number) => {
			if (editor != null) {
				// Insert the text at current position
				editor.chain().focus().insertContent(text).run();

				// If cursorOffset is provided (for block syntax), move cursor inside the block
				if (cursorOffset != null) {
					// Get current position and move back to inside the block
					const { from } = editor.state.selection;
					const newPos = from - (text.length - cursorOffset);

					editor.chain().setTextSelection(newPos).run();
				}

				// Update text content after insertion
				setTextContent(editor.getText());
			}
		},
		[editor],
	);

	if (!editor) {
		return null;
	}

	// The toolbar is assembled as groups so a restricted one still looks right: a group is
	// drawn only when the caller offers something in it, and the dividing rules are drawn
	// BETWEEN the surviving groups rather than at fixed points.
	// Every toolbar button is `type="button"`: the editor sits inside forms
	// (the storefront support form), where a button without a type submits
	// the form, so pressing Bold sent a half-written request (PD-16475).
	const toolbarGroups: Array<{ key: string; content: React.ReactNode }> = [];

	const addGroup = (
		key: string,
		isOffered: boolean,
		content: React.ReactNode,
	): void => {
		if (isOffered) {
			toolbarGroups.push({ key, content });
		}
	};

	addGroup(
		'marks',
		offers('bold') ||
			offers('italic') ||
			offers('underline') ||
			offers('strikethrough'),
		<>
			{offers('bold') && (
				<button
					type="button"
					onClick={() => editor.chain().focus().toggleBold().run()}
					className={`p-2 rounded transition-colors ${
						editor.isActive('bold')
							? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
							: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
					}`}
					title={t('Bold')}
				>
					<Icon icon={faBold} size="1x" />
				</button>
			)}

			{offers('italic') && (
				<button
					type="button"
					onClick={() => editor.chain().focus().toggleItalic().run()}
					className={`p-2 rounded transition-colors ${
						editor.isActive('italic')
							? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
							: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
					}`}
					title={t('Italic')}
				>
					<Icon icon={faItalic} size="1x" />
				</button>
			)}

			{offers('underline') && (
				<button
					type="button"
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					className={`p-2 rounded transition-colors ${
						editor.isActive('underline')
							? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
							: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
					}`}
					title={t('Underline')}
				>
					<Icon icon={faUnderline} size="1x" />
				</button>
			)}

			{offers('strikethrough') && (
				<button
					type="button"
					onClick={() => editor.chain().focus().toggleStrike().run()}
					className={`p-2 rounded transition-colors ${
						editor.isActive('strike')
							? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
							: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
					}`}
					title={t('Strikethrough')}
				>
					<Icon icon={faStrikethrough} size="1x" />
				</button>
			)}
		</>,
	);

	addGroup(
		'font',
		offers('font'),
		<>
			<Dropdown
				items={fontFamilies.map((font) => ({
					label: font.label,
					onClick: (): void => {
						editor.chain().focus().setFontFamily(font.value).run();
					},
				}))}
				align="left"
				trigger={
					<button
						type="button"
						className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
						title={t('Font Family')}
					>
						<Icon icon={faFont} size="1x" />
					</button>
				}
			/>

			<Dropdown
				items={fontSizes.map((size) => ({
					label: size.label,
					onClick: (): void => {
						editor
							.chain()
							.focus()
							.setMark('textStyle', { fontSize: size.value })
							.run();
					},
				}))}
				align="left"
				trigger={
					<button
						type="button"
						className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
						title={t('Font Size')}
					>
						<Icon icon={faTextHeight} size="1x" />
					</button>
				}
			/>
		</>,
	);

	addGroup(
		'color',
		offers('color'),
		<>
			<div className="relative">
				<button
					type="button"
					onClick={() => setShowColorPicker(!showColorPicker)}
					className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
					title={t('Text Color')}
				>
					<Icon icon={faPalette} size="1x" />
				</button>
				{showColorPicker && (
					<div
						className={`absolute top-full left-0 mt-1 p-2 rounded shadow-lg z-10 ${colors.classes.bgPrimary} ${colors.classes.borderBase} border`}
					>
						<div className="grid grid-cols-5 gap-1">
							{textColors.map((color) => (
								<button
									type="button"
									key={color}
									onClick={() => {
										editor.chain().focus().setColor(color).run();
										setShowColorPicker(false);
									}}
									className="w-6 h-6 rounded border border-strong"
									style={{ backgroundColor: color }}
								/>
							))}
						</div>
					</div>
				)}
			</div>

			<div className="relative">
				<button
					type="button"
					onClick={() => setShowHighlightPicker(!showHighlightPicker)}
					className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
					title={t('Highlight Color')}
				>
					<Icon icon={faHighlighter} size="1x" />
				</button>
				{showHighlightPicker && (
					<div
						className={`absolute top-full left-0 mt-1 p-2 rounded shadow-lg z-10 ${colors.classes.bgPrimary} ${colors.classes.borderBase} border`}
					>
						<div className="grid grid-cols-5 gap-1">
							{textColors.map((color) => (
								<button
									type="button"
									key={color}
									onClick={() => {
										editor
											.chain()
											.focus()
											.setMark('textStyle', { backgroundColor: color })
											.run();
										setShowHighlightPicker(false);
									}}
									className="w-6 h-6 rounded border border-strong"
									style={{ backgroundColor: color }}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</>,
	);

	addGroup(
		'lists',
		offers('lists'),
		<>
			<button
				type="button"
				onClick={() => editor.chain().focus().toggleBulletList().run()}
				className={`p-2 rounded transition-colors ${
					editor.isActive('bulletList')
						? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
						: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
				}`}
				title={t('Bullet List')}
			>
				<Icon icon={faListUl} size="1x" />
			</button>

			<button
				type="button"
				onClick={() => editor.chain().focus().toggleOrderedList().run()}
				className={`p-2 rounded transition-colors ${
					editor.isActive('orderedList')
						? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
						: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
				}`}
				title={t('Numbered List')}
			>
				<Icon icon={faListOl} size="1x" />
			</button>
		</>,
	);

	addGroup(
		'align',
		offers('align'),
		<>
			<button
				type="button"
				onClick={() => editor.chain().focus().setTextAlign('left').run()}
				className={`p-2 rounded transition-colors ${
					editor.isActive({ textAlign: 'left' })
						? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
						: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
				}`}
				title={t('Align Left')}
			>
				<Icon icon={faAlignLeft} size="1x" />
			</button>

			<button
				type="button"
				onClick={() => editor.chain().focus().setTextAlign('center').run()}
				className={`p-2 rounded transition-colors ${
					editor.isActive({ textAlign: 'center' })
						? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
						: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
				}`}
				title={t('Align Center')}
			>
				<Icon icon={faAlignCenter} size="1x" />
			</button>

			<button
				type="button"
				onClick={() => editor.chain().focus().setTextAlign('right').run()}
				className={`p-2 rounded transition-colors ${
					editor.isActive({ textAlign: 'right' })
						? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
						: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
				}`}
				title={t('Align Right')}
			>
				<Icon icon={faAlignRight} size="1x" />
			</button>

			<button
				type="button"
				onClick={() => editor.chain().focus().setTextAlign('justify').run()}
				className={`p-2 rounded transition-colors ${
					editor.isActive({ textAlign: 'justify' })
						? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
						: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
				}`}
				title={t('Justify')}
			>
				<Icon icon={faAlignJustify} size="1x" />
			</button>
		</>,
	);

	addGroup(
		'links',
		offers('link') || offers('image'),
		<>
			{offers('link') && (
				<button
					type="button"
					onClick={addLink}
					className={`p-2 rounded transition-colors ${
						editor.isActive('link')
							? `${colors.classes.bgPrimary} ${colors.classes.textPrimary}`
							: `${colors.classes.textPrimary} ${colors.classes.hoverButton}`
					}`}
					title={t('Add Link')}
				>
					<Icon icon={faLink} size="1x" />
				</button>
			)}

			{offers('link') && (
				<button
					type="button"
					onClick={() => editor.chain().focus().unsetLink().run()}
					disabled={!editor.isActive('link')}
					className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton} disabled:opacity-50`}
					title={t('Remove Link')}
				>
					<Icon icon={faUnlink} size="1x" />
				</button>
			)}

			{offers('image') && (
				<button
					type="button"
					onClick={insertImage}
					className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
					title={t('Insert Image')}
				>
					<Icon icon={faImage} size="1x" />
				</button>
			)}
		</>,
	);

	addGroup(
		'context-variables',
		contextAwareVariables != null && contextAwareVariables.length > 0,
		<ContextAwareMergeVariableDropdown
			rootVariables={contextAwareVariables ?? []}
			content={textContent}
			cursorPosition={cursorPosition}
			onInsert={handleContextAwareInsert}
			disabled={disabled}
		/>,
	);

	addGroup(
		'merge-variables',
		showMergeVariableButton &&
			(contextAwareVariables == null || contextAwareVariables.length === 0),
		<div className="relative">
			<button
				type="button"
				onClick={() => {
					setShowVariablePicker(!showVariablePicker);
				}}
				className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
				title={t('Insert Merge Variable')}
			>
				<Icon icon={faBracketsCurly} size="1x" />
			</button>
			{showVariablePicker && availableVariables.length > 0 && (
				<div
					// right-0: the trigger sits late in the toolbar, so a
					// left-aligned menu juts past narrow editors (e.g. the
					// PDF builder's settings panel) and clips its entries.
					className={`absolute top-full right-0 mt-1 p-2 rounded shadow-lg z-10 min-w-64 max-h-64 overflow-y-auto ${colors.classes.bgPrimary} ${colors.classes.borderDefault} border`}
				>
					<div className="space-y-1">
						{availableVariables.map((variable, index) => (
							<button
								type="button"
								key={index}
								onClick={() => {
									insertVariable(variable.name);
								}}
								className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
								title={variable.description ?? ''}
							>
								<div className="font-mono">{`{{${variable.name}}}`}</div>
								{variable.description != null && (
									<div
										className={`text-xs ${colors.classes.textSecondary} truncate`}
									>
										{variable.description}
									</div>
								)}
							</button>
						))}
					</div>
				</div>
			)}
		</div>,
	);

	addGroup(
		'clear',
		offers('clear'),
		<button
			type="button"
			onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
			className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton}`}
			title={t('Clear Formatting')}
		>
			<Icon icon={faEraser} size="1x" />
		</button>,
	);

	addGroup(
		'history',
		offers('history'),
		<>
			<button
				type="button"
				onClick={() => editor.chain().focus().undo().run()}
				disabled={!canUndo}
				className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton} disabled:opacity-50`}
				title={t('Undo')}
			>
				<Icon icon={faUndo} size="1x" />
			</button>

			<button
				type="button"
				onClick={() => editor.chain().focus().redo().run()}
				disabled={!canRedo}
				className={`p-2 rounded transition-colors ${colors.classes.textPrimary} ${colors.classes.hoverButton} disabled:opacity-50`}
				title={t('Redo')}
			>
				<Icon icon={faRedo} size="1x" />
			</button>
		</>,
	);

	return (
		<div
			className={`border rounded-lg overflow-hidden flex flex-col h-full ${colors.classes.borderDefault}`}
		>
			{/* Toolbar - only show when not disabled */}
			{!disabled && (
				<div
					className={`flex-shrink-0 flex items-center gap-1 p-2 border-b flex-wrap ${colors.classes.bgSecondary} ${colors.classes.borderBase}`}
				>
					{toolbarGroups.map((group, index) => (
						<Fragment key={group.key}>
							{index > 0 && (
								<div className={`w-px h-6 mx-1 ${colors.classes.borderBase}`} />
							)}
							{group.content}
						</Fragment>
					))}
				</div>
			)}

			{/* Editor Content */}
			<div
				className={`flex-1 min-h-0 overflow-auto ${disabled ? '' : 'cursor-text'}`}
				onMouseDown={handleSurfaceMouseDown}
			>
				<div className="relative">
					{placeholder !== '' && isEmpty && (
						<div
							aria-hidden="true"
							className="pointer-events-none absolute left-0 top-0 p-4 text-sm text-faint"
						>
							{t(placeholder)}
						</div>
					)}
					<EditorContent editor={editor} />
				</div>
			</div>

			{/* Click outside handlers to close dropdowns */}
			{(showColorPicker || showHighlightPicker || showVariablePicker) && (
				<div
					className="fixed inset-0 z-0"
					onClick={() => {
						setShowColorPicker(false);
						setShowHighlightPicker(false);
						setShowVariablePicker(false);
					}}
				/>
			)}

			{usesMediaLibrary && (
				<MediaPickerModal
					isOpen={isMediaPickerOpen}
					allowedTypes={['image', 'svg']}
					onClose={() => {
						setIsMediaPickerOpen(false);
					}}
					onChoose={insertLibraryPicture}
				/>
			)}
		</div>
	);
};
